// Coach voice for Emit-based apps (the Chaplain app): the same voices as SAFE, through our server.
// A reply is cut into pieces of a few sentences; the next piece is fetched while one plays.
// Any failure hands the rest of the reply to the device voice, so speech never just stops.
import { splitForSpeech } from './speechChunking';

const SILENT_WAV = 'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';
const SAMPLE_TEXT = "Hello. I'm here to listen whenever you're ready, so take your time.";

export const coachVoice = { run: 0, audio: null, unlocked: false, unlocking: false, off: false, choice: null };

const token = () => { try { return localStorage.getItem('token'); } catch (e) { return null; } };
const player = () => {
  if (!coachVoice.audio && typeof Audio !== 'undefined') {
    coachVoice.audio = new Audio();
    coachVoice.audio.setAttribute('playsinline', '');
    coachVoice.audio.preload = 'auto';
  }
  return coachVoice.audio;
};

// Phones only let a page play sound it started from a tap; the first tap unlocks the player.
let unlockReady = false;
export const prepareCoachVoice = () => {
  if (unlockReady || typeof document === 'undefined') return;
  unlockReady = true;
  const unlock = () => {
    const a = player();
    if (!a || coachVoice.unlocked || coachVoice.unlocking) return;
    if (a.src && !a.paused) return;
    coachVoice.unlocking = true;
    a.src = SILENT_WAV;
    const done = (ok) => { coachVoice.unlocking = false; coachVoice.unlocked = ok; };
    let p;
    try { p = a.play(); } catch (e) { done(false); return; }
    if (p && p.then) p.then(() => done(true), () => done(false)); else done(true);
  };
  ['touchend', 'click', 'keydown', 'pointerup'].forEach((ev) => document.addEventListener(ev, unlock, true));
};

export const coachVoiceActive = () => !coachVoice.off && !!coachVoice.choice && coachVoice.choice !== 'device';

// The device voice Emit has always used.
export const pickDeviceVoice = (voices) =>
  (voices || []).find((v) => (v.lang || '').startsWith('en') && (v.name || '').toLowerCase().includes('male')) || null;

const voiceText = (t) => String(t || '')
  .replace(/MSTAG[\s\S]*?ENDTAG/g, ' ')
  .replace(/^#{1,6}\s+(.+?)\s*$/gm, '$1.')
  .replace(/^\s*-{3,}\s*$/gm, ' ')
  .replace(/^\s*[-*\u2022]\s+/gm, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/FHO-OS/gi, 'foe OS').replace(/\bFHO\b/gi, 'foe')
  .replace(/\s+/g, ' ').trim();

const voiceChunks = (t) => {
  const out = [];
  let cur = '';
  for (const p of splitForSpeech(t)) {
    if (cur && cur.length + 1 + p.length > 420) { out.push(cur); cur = p; } else { cur = cur ? cur + ' ' + p : p; }
  }
  if (cur) out.push(cur);
  return out;
};

const fetchVoice = (text) => fetch('/api/v1/safe/voice', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
  body: JSON.stringify({ text }),
}).then((r) => {
  if (r.status === 503 || r.status === 403) coachVoice.off = true;   // voice service not set up
  if (!r.ok) throw new Error('voice ' + r.status);
  return r.blob();
}).then((b) => URL.createObjectURL(b));

export const loadCoachVoices = () => fetch('/api/v1/safe/voices', { headers: { Authorization: 'Bearer ' + token() } })
  .then((r) => (r.ok ? r.json() : null))
  .then((d) => {
    if (!d || !Array.isArray(d.voices)) return null;
    coachVoice.choice = d.choice;
    return d;
  })
  .catch(() => null);

export const saveCoachVoice = (id) => {
  stopCoachVoice();
  coachVoice.choice = id;
  if (id !== 'device') coachVoice.off = false;
  return fetch('/api/v1/safe/voice-choice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
    body: JSON.stringify({ choice: id }),
  }).catch(() => {});
};

export const stopCoachVoice = () => {
  coachVoice.run++;
  const a = coachVoice.audio;
  if (a) { try { a.pause(); } catch (e) { /* ignore */ } }
};

export const previewCoachVoice = (id) => {
  stopCoachVoice();
  try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ }
  if (id === 'device') {
    try {
      const u = new window.SpeechSynthesisUtterance(SAMPLE_TEXT);
      u.rate = 0.9; u.pitch = 0.85;
      const pv = pickDeviceVoice(window.speechSynthesis.getVoices());
      if (pv) u.voice = pv;
      window.speechSynthesis.speak(u);
    } catch (e) { /* no speech */ }
    return;
  }
  const mine = ++coachVoice.run;
  fetch('/api/v1/safe/voice-sample/' + encodeURIComponent(id), { headers: { Authorization: 'Bearer ' + token() } })
    .then((r) => { if (!r.ok) throw new Error('sample ' + r.status); return r.blob(); })
    .then((b) => {
      if (mine !== coachVoice.run) return;
      const a = player();
      const url = URL.createObjectURL(b);
      a.onended = () => URL.revokeObjectURL(url);
      a.onerror = () => URL.revokeObjectURL(url);
      a.src = url;
      a.play().catch(() => {});
    })
    .catch((e) => console.warn('[coach voice] sample failed:', e && e.message));
};

export const playCoachVoice = (text, onStart, onEnd, onFail) => {
  const mine = ++coachVoice.run;
  const parts = voiceChunks(voiceText(text));
  const a = player();
  if (!a || !parts.length) { onEnd(); return; }
  let i = 0;
  let pending = fetchVoice(parts[0]);
  const step = () => {
    if (mine !== coachVoice.run) return;
    if (i >= parts.length) { onEnd(); return; }
    const n = i++;
    const here = pending;
    pending = i < parts.length ? fetchVoice(parts[i]) : null;
    if (pending) pending.catch(() => {});
    here.then((url) => {
      if (mine !== coachVoice.run) { URL.revokeObjectURL(url); return null; }
      a.onended = () => { URL.revokeObjectURL(url); step(); };
      a.onerror = () => { URL.revokeObjectURL(url); step(); };
      a.src = url;
      return a.play().then(() => { if (n === 0) onStart(); });
    }).catch((err) => {
      if (mine !== coachVoice.run) return;
      console.warn('[coach voice] using the device voice for the rest of this reply:', err && err.message);
      coachVoice.run++;
      onFail(parts.slice(n).join(' '));
    });
  };
  step();
};
